/**
 * Reconnection Manager
 * Handles WebSocket reconnection with exponential backoff.
 * Extracted from websocket.js for better separation of concerns.
 */

/**
 * Create a reconnection manager
 * @param {Object} options
 * @param {number} options.initialDelay - Initial reconnect delay in ms (default 1000)
 * @param {number} options.maxDelay - Maximum delay between attempts in ms (default 30000)
 * @param {number} options.offlineThreshold - Time in ms after which to report offline status (default 30000)
 * @param {Function} options.onReconnect - Callback to perform the actual reconnection
 * @param {Function} options.onOfflineStatusChange - Callback when offline status changes (receives boolean)
 * @param {Function} options.log - Callback for logging (optional)
 * @returns {Object} Reconnection manager API
 */
export function createReconnectionManager(options) {
  const {
    initialDelay = 1000,
    maxDelay = 30000,
    offlineThreshold = 30000,
    onReconnect,
    onOfflineStatusChange,
    onGiveUp,
    maxAttemptsWithoutSuccess = null, // null = retry forever
    log = () => {},
    initialOffline = false, // Start in offline mode (preserves state across doc switches)
  } = options;

  // Internal state
  let attempts = 0;
  let timer = null;
  let firstFailureTime = initialOffline ? Date.now() - offlineThreshold : null;
  let isOffline = initialOffline;
  let stopped = false;
  // Tracks whether ANY connect attempt ever succeeded since this manager
  // was created. While false, we can give up after maxAttemptsWithoutSuccess
  // — useful when the server consistently rejects (e.g. revoked WS auth)
  // so we don't reconnect-loop forever for a doc the user can't access.
  // Network blips after a successful connect still retry forever.
  let everConnected = false;
  // Until when the server asked us to wait (schedule's minDelay, e.g. too
  // many sockets for this account): a wake-up before then is ignored.
  // Otherwise two sockets refused right after opening wake each other
  // (a socket reaching CONNECTED wakes the others) and hammer the server.
  let holdUntil = 0;

  /**
   * Update offline status and notify if changed
   * @param {boolean} offline - New offline status
   */
  function setOfflineStatus(offline) {
    if (isOffline !== offline) {
      isOffline = offline;
      if (onOfflineStatusChange) {
        onOfflineStatusChange(offline);
      }
    }
  }

  /**
   * Schedule a reconnection attempt with exponential backoff
   * Retries forever once we've connected at least once. Before that,
   * gives up after maxAttemptsWithoutSuccess so a doc the user can no
   * longer access (server returns 403 on every upgrade) doesn't keep
   * the WebSocket loop noisy forever.
   */
  function schedule({ minDelay = 0 } = {}) {
    // Prevent double-scheduling
    if (stopped || timer) return;

    // Auth-failure detection: if we've never successfully connected and
    // we've already retried beyond the cap, treat the doc as inaccessible.
    if (
      maxAttemptsWithoutSuccess !== null
      && !everConnected
      && attempts >= maxAttemptsWithoutSuccess
    ) {
      log(`Giving up after ${attempts} attempts without ever connecting (likely access revoked)`);
      stopped = true;
      if (onGiveUp) {
        try { onGiveUp(); } catch (err) {
          // Don't let a bad callback prevent the manager from staying stopped
          log(`onGiveUp threw: ${err && err.message}`);
        }
      }
      return;
    }

    // Track when failures started
    if (firstFailureTime === null) {
      firstFailureTime = Date.now();
    }

    // After threshold, report offline status but keep trying
    const failingForMs = Date.now() - firstFailureTime;
    if (failingForMs >= offlineThreshold) {
      setOfflineStatus(true);
    }

    // Exponential backoff with jitter, capped at maxDelay
    // `minDelay`: the server asked us to back off (e.g. too many sockets).
    const delay = Math.max(minDelay, Math.min(
      initialDelay * Math.pow(2, attempts) + Math.random() * 1000,
      maxDelay,
    ));
    if (minDelay > 0) holdUntil = Date.now() + delay;

    log(`Reconnecting in ${Math.round(delay)}ms (attempt ${attempts + 1})`);

    timer = setTimeout(async () => {
      timer = null;
      attempts++;

      if (onReconnect) {
        try {
          await onReconnect();
          // Successful reconnection - reset state
          reset();
        } catch (error) {
          // Failed - schedule another attempt
          schedule();
        }
      }
    }, delay);
  }

  /**
   * Cancel any pending reconnection timer
   */
  function cancel() {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  }

  /**
   * Reset the reconnection state (call after successful connection).
   * Also marks the manager as "has connected at least once" so future
   * disconnections retry forever (network blips) regardless of the
   * maxAttemptsWithoutSuccess cap.
   */
  function reset() {
    cancel();
    holdUntil = 0;
    attempts = 0;
    firstFailureTime = null;
    everConnected = true;
    setOfflineStatus(false);
  }

  /**
   * Trigger an immediate reconnection attempt
   * Resets the attempt counter and skips backoff delay
   */
  function triggerImmediate() {
    if (stopped) return;
    if (Date.now() < holdUntil) {
      log(`Not reconnecting now: the server asked to wait ${Math.round((holdUntil - Date.now()) / 1000)}s more`);
      return;
    }

    log("Triggering immediate reconnection");

    // Cancel any pending timer
    cancel();

    // Reset for fresh start
    attempts = 0;
    firstFailureTime = null;

    // Immediately try to reconnect
    if (onReconnect) {
      onReconnect()
        .then(() => {
          reset();
        })
        .catch(() => {
          // If immediate attempt fails, fall back to normal backoff
          schedule();
        });
    }
  }

  /**
   * Stop the reconnection manager (prevents further reconnection attempts)
   */
  function stop() {
    stopped = true;
    cancel();
  }

  /**
   * Re-enable the reconnection manager after being stopped
   */
  function enable() {
    stopped = false;
  }

  /**
   * Check if the manager is currently stopped
   * @returns {boolean}
   */
  function isStopped() {
    return stopped;
  }

  /**
   * Check if currently in offline state
   * @returns {boolean}
   */
  function isInOfflineState() {
    return isOffline;
  }

  /**
   * Get current attempt count
   * @returns {number}
   */
  function getAttempts() {
    return attempts;
  }

  return {
    schedule,
    cancel,
    reset,
    triggerImmediate,
    stop,
    enable,
    isStopped,
    isInOfflineState,
    getAttempts,
  };
}
